import {Identifier} from "../common/Identifier";
import {Nameable} from "../common/Nameable";
import {Module} from "../common/Module";
import {isPrivileged} from "../../config/Privileges";
import {ProjectAreaName} from "./Project";
import {UserPrivileges} from "../users/User";
import {UserGroupId} from "../user-groups/UserGroup";
import {Sensitive} from "../common/Sensitive";
import {Money} from "../accounting/Finance";
import {PriceRange} from "../accounting/PriceRange";
import {SoftDeletable} from "../common/SoftDeletable";
import {IdAndValue} from "../common/IdAndValue";


/**
 * Represents a module for managing roles associated with a project.
 *
 * The purpose of this module is to facilitate role-based access control (RBAC) within a specific project context,
 * enabling the application to handle privileges for users effectively. It helps ensure that users can access
 * and perform actions within their associated roles.
 */
export const ProjectRoleModule: Module = {
    identifier: "project_roles",
    isPrivileged: ( authUser, privilege) =>
        isPrivileged( authUser, ProjectAreaName, ProjectRoleModule.identifier, privilege  )
};

/**
 * Represents the unique identifier for a specific role within a project.
 *
 * This interface provides a clear and structured way to associate an identifier with a role,
 * ensuring consistency and type safety across the application when dealing with project roles.
 *
 * @property projectRoleId - A unique identifier of type `Identifier<number>` associated with a project role.
 * It enforces type safety while allowing consistent identification of roles.
 */
export interface ProjectRoleId {
    projectRoleId: Identifier<number>
}

/**
 * Represents a simplified view of a role within a project.
 *
 * This interface is used to identify key attributes of roles
 * within a project, specifically highlighting whether a role
 * is associated with a project lead position.
 *
 * @*/
/**
 * Represents a simplified view of a role within a project.
 *
 * This interface is used to identify key attributes of roles
 * within a project, specifically highlighting whether a role
 * is associated with a project lead position.
 *
 * @extends ProjectRoleId Identifies the unique ID associated with the role.
 * @extends Nameable Provides a human-readable name for the role.
 */
export interface ProjectRoleShort extends ProjectRoleId, Nameable {
    isProjectLead: boolean
}

/**
 * Represents a concise mapping of a project role for use in various contexts.
 *
 * This interface provides a simplified structure for representing a project role
 * by encapsulating the minimum required details, making it suitable for lightweight
 * operations or UI contexts requiring role information.
 */
export interface ProjectRoleShortProp {
    projectRole: ProjectRoleShort
}

/**
 * ProjectRole
 *
 * employees can be added to projects through project roles.
 * these roles defines privileges, prices and can have qualification requirements
 */
export interface ProjectRole extends ProjectRoleShort, SoftDeletable {
    /** the privileges of the role */
    privileges: UserPrivileges
    /** the default time rate per hour */
    defaultPrice: Sensitive< Money >
    /** the range in which the time rate is changeable */
    priceRange: Sensitive< PriceRange >
    /** user group associated with the role, with it privileges are associated  */
    userGroup: IdAndValue< Identifier< number >, string >
    /** the qualifications the employee has to have to be put in this role */
    qualifications: IdAndValue< Identifier< number >, string>[]
}

/**
 * Represents the association between a project and its designated role.
 *
 * This type ensures that every project is linked to a specific role, helping to define responsibilities and permissions within the project's context.
 *
 * @property projectRole - The specific role associated with the project, used to determine role-based behaviors, permissions, or constraints.
 */
export type ProjectRoleProp = {
    projectRole: ProjectRole
}

/**
 * Represents a request to manage or update a project role within the system.
 *
 * This interface is used for transferring data related to project roles with necessary modifications,
 * excluding certain fields such as `projectRoleId`, `privileges`, and other system-reserved properties
 * from the `ProjectRole` interface. It provides the minimal required set of parameters for frontend-
 * backend communication regarding project role updates or creation.
 *
 * The modifications allow more control and customization of project roles while simplifying the payload
 * for the API.
 */
export interface ProjectRoleRequest extends Omit< ProjectRole, 'projectRoleId' | 'privileges' | 'isDeleted' | 'defaultPrice' | 'priceRange' | 'userGroup' | 'qualifications'> {
    id: number
    priceRange: PriceRange
    defaultPrice: Money
    userGroup: IdAndValue<number, string>
    qualifications:  IdAndValue< number, string>[]
}

/**
 * Represents a property that encapsulates a project role request for a project.
 *
 * This interface is used to structure the data related to a role request in the context of a project, ensuring a consistent format across the application.
 * It acts as a container for the `ProjectRoleRequest` object, providing a predictable interface for consumers of this property.
 *
 * @property projectRoleReq - The details of the role request, encapsulated within the `ProjectRoleRequest` object.
 *                            This includes information about the specific role requested and any associated metadata.
 */
export interface ProjectRoleRequestProp {
    projectRoleReq: ProjectRoleRequest
}